import { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  PauseCircle,
  PlayCircle,
  Star,
  UserCheck,
  X,
  XCircle,
} from 'lucide-react';
import {
  INTERVENTION_ETATS_FINAUX,
  INTERVENTION_MODE_AFFECTATION,
  type InterventionModeAffectation,
  type InterventionEtatFinal,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { etatFinalLabel } from './badges';

/**
 * Famille de mini-modals déclenchés depuis le panel "Workflow" du drawer
 * Intervention (US-INT-03 / US-INT-05 / US-INT-07).
 *
 * Chaque action expose une signature uniforme : interventionId + onClose + onSaved.
 */

interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface PrestataireOption {
  id: string;
  nom: string;
  specialite: string | null;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const MODE_AFFECTATION_LABELS: Record<InterventionModeAffectation, string> = {
  automatique: 'Automatique',
  manuelle: 'Manuelle',
  auto_affectation: 'Auto-affectation',
  equipe: 'Équipe',
  externe: 'Prestataire externe',
};

function toIsoOrNull(v: string | null | undefined): string | null {
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch {
    return null;
  }
}

// ─── Affecter (US-INT-05) ───────────────────────────────────────────────────

export function AffecterModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);
  const [prestataires, setPrestataires] = useState<PrestataireOption[]>([]);
  const [mode, setMode] = useState<InterventionModeAffectation>('manuelle');
  const [technicienId, setTechnicienId] = useState('');
  const [prestataireId, setPrestataireId] = useState('');
  const [equipeIds, setEquipeIds] = useState<string[]>([]);
  const [datePlanifiee, setDatePlanifiee] = useState('');
  const [dureeEstimee, setDureeEstimee] = useState('');
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
    api
      .get<{ data: PrestataireOption[] }>('/prestataires?is_active=true&page_size=200')
      .then((res) => setPrestataires(res.data.data))
      .catch(() => setPrestataires([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    setWarnings([]);
    try {
      const payload: Record<string, unknown> = {
        mode_affectation: mode,
        date_planifiee: toIsoOrNull(datePlanifiee),
        duree_estimee: dureeEstimee === '' ? null : Number(dureeEstimee),
      };
      if (mode === 'externe') {
        payload.prestataire_id = prestataireId || null;
        payload.technicien_affecte_id = null;
      } else if (mode === 'equipe') {
        payload.equipe_affectee = equipeIds;
        payload.technicien_affecte_id = technicienId || null;
      } else {
        payload.technicien_affecte_id = technicienId || null;
      }

      const res = await api.post<{
        data: unknown;
        warnings?: string[];
      }>(`/interventions/${interventionId}/affecter`, payload);
      if (res.data.warnings && res.data.warnings.length > 0) {
        setWarnings(res.data.warnings);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function toggleEquipeMember(id: string) {
    setEquipeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <UserCheck className="w-4 h-4 mr-2 text-primary-600" />
              Affecter l'intervention
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3 overflow-y-auto">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            {warnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
                {warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">
                    ⚠ {w}
                  </p>
                ))}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mode d'affectation *
              </label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as InterventionModeAffectation)}
                className={inputClass}
                required
              >
                {INTERVENTION_MODE_AFFECTATION.map((m) => (
                  <option key={m} value={m}>
                    {MODE_AFFECTATION_LABELS[m]}
                  </option>
                ))}
              </select>
            </div>
            {mode !== 'externe' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Technicien {mode === 'equipe' ? "(chef d'équipe)" : ''}
                </label>
                <select
                  value={technicienId}
                  onChange={(e) => setTechnicienId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">— Aucun —</option>
                  {personnels.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {mode === 'equipe' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Membres de l'équipe
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                  {personnels.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center space-x-2 text-sm text-gray-700 hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={equipeIds.includes(p.id)}
                        onChange={() => toggleEquipeMember(p.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        {p.last_name} {p.first_name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {mode === 'externe' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Prestataire externe *
                </label>
                <select
                  value={prestataireId}
                  onChange={(e) => setPrestataireId(e.target.value)}
                  className={inputClass}
                  required
                >
                  <option value="">— Sélectionner —</option>
                  {prestataires.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                      {p.specialite ? ` — ${p.specialite}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Date planifiée
                </label>
                <input
                  type="datetime-local"
                  value={datePlanifiee}
                  onChange={(e) => setDatePlanifiee(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Durée estimée (min)
                </label>
                <input
                  type="number"
                  min={1}
                  value={dureeEstimee}
                  onChange={(e) => setDureeEstimee(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Affecter
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─── Planifier ──────────────────────────────────────────────────────────────

export function PlanifierModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [datePlanifiee, setDatePlanifiee] = useState('');
  const [dureeEstimee, setDureeEstimee] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const iso = toIsoOrNull(datePlanifiee);
    if (!iso) {
      setErrorMessage('La date planifiée est obligatoire.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/${interventionId}/planifier`, {
        date_planifiee: iso,
        duree_estimee: dureeEstimee === '' ? null : Number(dureeEstimee),
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SimpleModalShell
      title="Planifier l'intervention"
      onClose={onClose}
      saving={saving}
      onSubmit={submit}
      submitLabel="Planifier"
      errorMessage={errorMessage}
    >
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Date planifiée *</label>
        <input
          type="datetime-local"
          value={datePlanifiee}
          onChange={(e) => setDatePlanifiee(e.target.value)}
          className={inputClass}
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Durée estimée (minutes)
        </label>
        <input
          type="number"
          min={1}
          value={dureeEstimee}
          onChange={(e) => setDureeEstimee(e.target.value)}
          className={inputClass}
        />
      </div>
    </SimpleModalShell>
  );
}

// ─── Démarrer ───────────────────────────────────────────────────────────────

export function DemarrerModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [diagnosticInitial, setDiagnosticInitial] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/${interventionId}/demarrer`, {
        diagnostic_initial: diagnosticInitial.trim() || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SimpleModalShell
      title="Démarrer l'intervention"
      icon={<PlayCircle className="w-4 h-4 mr-2 text-primary-600" />}
      onClose={onClose}
      saving={saving}
      onSubmit={submit}
      submitLabel="Démarrer"
      errorMessage={errorMessage}
    >
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Diagnostic initial (optionnel)
        </label>
        <textarea
          value={diagnosticInitial}
          onChange={(e) => setDiagnosticInitial(e.target.value)}
          rows={4}
          className={inputClass}
          placeholder="Premières observations sur place…"
        />
      </div>
    </SimpleModalShell>
  );
}

// ─── Pause ──────────────────────────────────────────────────────────────────

export function PauseModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!motif.trim()) {
      setErrorMessage('Le motif est obligatoire.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/${interventionId}/pause`, {
        motif: motif.trim(),
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SimpleModalShell
      title="Mettre en pause"
      icon={<PauseCircle className="w-4 h-4 mr-2 text-orange-500" />}
      onClose={onClose}
      saving={saving}
      onSubmit={submit}
      submitLabel="Mettre en pause"
      errorMessage={errorMessage}
    >
      <p className="text-xs text-gray-500">L'intervention passera en statut "en attente pièces".</p>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Motif *</label>
        <textarea
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          rows={3}
          className={inputClass}
          required
          placeholder="Ex. : Attente de la livraison de la pièce X."
        />
      </div>
    </SimpleModalShell>
  );
}

// ─── Annuler ────────────────────────────────────────────────────────────────

export function AnnulerModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!motif.trim()) {
      setErrorMessage('Le motif est obligatoire.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/${interventionId}/annuler`, {
        motif: motif.trim(),
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SimpleModalShell
      title="Annuler l'intervention"
      icon={<XCircle className="w-4 h-4 mr-2 text-red-500" />}
      onClose={onClose}
      saving={saving}
      onSubmit={submit}
      submitLabel="Confirmer l'annulation"
      submitTone="red"
      errorMessage={errorMessage}
    >
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Motif *</label>
        <textarea
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          rows={3}
          className={inputClass}
          required
        />
      </div>
    </SimpleModalShell>
  );
}

// ─── Clôturer ───────────────────────────────────────────────────────────────

export function CloturerModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [diagnosticFinal, setDiagnosticFinal] = useState('');
  const [causePanne, setCausePanne] = useState('');
  const [actionsRealisees, setActionsRealisees] = useState('');
  const [etatFinal, setEtatFinal] = useState<InterventionEtatFinal>('fonctionnel');
  const [coutMainOeuvre, setCoutMainOeuvre] = useState('');
  const [coutPieces, setCoutPieces] = useState('');
  const [coutDeplacement, setCoutDeplacement] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!diagnosticFinal.trim() || !actionsRealisees.trim()) {
      setErrorMessage('Diagnostic final et actions réalisées sont obligatoires.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/${interventionId}/cloturer`, {
        diagnostic_final: diagnosticFinal.trim(),
        cause_panne: causePanne.trim() || null,
        actions_realisees: actionsRealisees.trim(),
        etat_final: etatFinal,
        cout_main_oeuvre: coutMainOeuvre === '' ? null : Number(coutMainOeuvre),
        cout_pieces: coutPieces === '' ? null : Number(coutPieces),
        cout_deplacement: coutDeplacement === '' ? null : Number(coutDeplacement),
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
              Clôturer l'intervention
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3 overflow-y-auto">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Diagnostic final *
              </label>
              <textarea
                value={diagnosticFinal}
                onChange={(e) => setDiagnosticFinal(e.target.value)}
                rows={3}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Cause de la panne
              </label>
              <textarea
                value={causePanne}
                onChange={(e) => setCausePanne(e.target.value)}
                rows={2}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Actions réalisées *
              </label>
              <textarea
                value={actionsRealisees}
                onChange={(e) => setActionsRealisees(e.target.value)}
                rows={3}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                État final de l'équipement *
              </label>
              <select
                value={etatFinal}
                onChange={(e) => setEtatFinal(e.target.value as InterventionEtatFinal)}
                className={inputClass}
                required
              >
                {INTERVENTION_ETATS_FINAUX.map((s) => (
                  <option key={s} value={s}>
                    {etatFinalLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Coût main d'œuvre
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={coutMainOeuvre}
                  onChange={(e) => setCoutMainOeuvre(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Coût pièces</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={coutPieces}
                  onChange={(e) => setCoutPieces(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Coût déplacement
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={coutDeplacement}
                  onChange={(e) => setCoutDeplacement(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-green-500 hover:bg-green-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Clôturer
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─── Évaluer (US-INT-07) ────────────────────────────────────────────────────

export function EvaluerModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [note, setNote] = useState(5);
  const [delai, setDelai] = useState<number | null>(null);
  const [qualite, setQualite] = useState<number | null>(null);
  const [comportement, setComportement] = useState<number | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/${interventionId}/evaluer`, {
        note_satisfaction: note,
        evaluation_delai: delai,
        evaluation_qualite: qualite,
        evaluation_comportement: comportement,
        commentaire_satisfaction: commentaire.trim() || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Star className="w-4 h-4 mr-2 text-yellow-500" />
              Évaluer l'intervention
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-4 overflow-y-auto">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Note globale *</label>
              <StarPicker value={note} onChange={setNote} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SubNote label="Délai" value={delai} onChange={setDelai} />
              <SubNote label="Qualité" value={qualite} onChange={setQualite} />
              <SubNote label="Comportement" value={comportement} onChange={setComportement} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Commentaire</label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                rows={3}
                className={inputClass}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Envoyer mon évaluation
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

function StarPicker({ value, onChange }: { value: number; onChange(n: number): void }) {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          className="focus:outline-none"
          aria-label={`${i} étoile${i > 1 ? 's' : ''}`}
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              i <= value ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function SubNote({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange(v: number | null): void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(value === i ? null : i)}
            className="focus:outline-none"
            aria-label={`${i} étoile${i > 1 ? 's' : ''}`}
          >
            <Star
              className={`w-4 h-4 transition-colors ${
                value !== null && i <= value ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── SimpleModalShell — coquille mini-modal ────────────────────────────────

function SimpleModalShell({
  title,
  icon,
  onClose,
  saving,
  onSubmit,
  submitLabel,
  submitTone = 'primary',
  errorMessage,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  onClose(): void;
  saving: boolean;
  onSubmit(e: React.FormEvent): void;
  submitLabel: string;
  submitTone?: 'primary' | 'red' | 'green';
  errorMessage: string | null;
  children: React.ReactNode;
}) {
  const toneClass =
    submitTone === 'red'
      ? 'bg-red-500 hover:bg-red-600'
      : submitTone === 'green'
        ? 'bg-green-500 hover:bg-green-600'
        : 'bg-primary-500 hover:bg-primary-600';
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              {icon}
              {title}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            {children}
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white disabled:opacity-50 ${toneClass}`}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
